/**
 * Hook personalizado para gestión de permisos
 * Sistema Klever Octopus - Betel Natural
 */
import { useState, useEffect, useCallback } from 'react';
import permissionsService from '../services/permissionsService';
import { useDebounce } from './useDebounce';

export const usePermissions = (initialParams = {}) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50
  });

  // Parámetros de filtrado
  const [filters, setFilters] = useState({
    search: '',
    module: '',
    group: '',
    sort_by: 'name',
    sort_order: 'asc',
    ...initialParams
  });

  // Debounce para búsqueda
  const debouncedSearch = useDebounce(filters.search, 500);

  // Cargar permisos
  const loadPermissions = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      console.log('=== usePermissions: Loading permissions ===');
      const response = await permissionsService.getPermissions();
      console.log('Response from permissionsService:', response);

      if (response.success) {
        // El nuevo sistema RBAC devuelve {total, permissions}
        // Cada permiso ya viene con su estructura completa desde el backend
        let permissionsArray = [];

        if (response.data) {
          // Si viene en formato {total, permissions}
          if (response.data.permissions && Array.isArray(response.data.permissions)) {
            permissionsArray = response.data.permissions;
            console.log('Extracted permissions array:', permissionsArray);
          }
          // Si viene directamente como array
          else if (Array.isArray(response.data)) {
            permissionsArray = response.data;
            console.log('Direct permissions array:', permissionsArray);
          }
          // Formato viejo (fallback)
          else if (typeof response.data === 'object' && !Array.isArray(response.data)) {
            console.log('Old format detected, converting...');
            const uniquePermissions = new Set();

            // Iterar sobre cada rol
            Object.entries(response.data).forEach(([roleName, roleData]) => {
              if (roleData.permissions && Array.isArray(roleData.permissions)) {
                // Agregar cada permiso del rol
                roleData.permissions.forEach(permName => {
                  if (!uniquePermissions.has(permName)) {
                    uniquePermissions.add(permName);

                    // Crear objeto de permiso normalizado
                    permissionsArray.push({
                      id: permName,
                      name: permName,
                      display_name: getPermissionDisplayName(permName),
                      module: inferModuleFromPermission(permName),
                      group: inferGroupFromPermission(permName),
                      description: getPermissionDescription(permName),
                      roles: [roleName]
                    });
                  } else {
                    // Si ya existe, agregar el rol a la lista
                    const existing = permissionsArray.find(p => p.name === permName);
                    if (existing && !existing.roles.includes(roleName)) {
                      existing.roles.push(roleName);
                    }
                  }
                });
              }
            });
          }
        }

        console.log('Final permissions array:', permissionsArray);
        console.log('Total permissions:', permissionsArray.length);

        setPermissions(permissionsArray);
        setPagination(prev => ({
          ...prev,
          totalItems: permissionsArray.length
        }));
      } else {
        console.log('Response not successful:', response.message);
        setError(response.message);
        setPermissions([]);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      setError('Error cargando permisos');
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear permiso
  const createPermission = async (permissionData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await permissionsService.createPermission(permissionData);

      if (response.success) {
        await loadPermissions(); // Recargar lista
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error creating permission:', error);
      const errorResponse = { success: false, message: 'Error creando permiso' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Actualizar permiso
  const updatePermission = async (id, permissionData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await permissionsService.updatePermission(id, permissionData);

      if (response.success) {
        // Actualizar permiso en la lista local
        setPermissions(prev => prev.map(permission => 
          permission.id === id ? { ...permission, ...response.data } : permission
        ));
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error updating permission:', error);
      const errorResponse = { success: false, message: 'Error actualizando permiso' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Eliminar permiso
  const deletePermission = async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await permissionsService.deletePermission(id);

      if (response.success) {
        // Remover permiso de la lista local
        setPermissions(prev => prev.filter(permission => permission.id !== id));
        return response;
      } else {
        setError(response.message);
        return response;
      }
    } catch (error) {
      console.error('Error deleting permission:', error);
      const errorResponse = { success: false, message: 'Error eliminando permiso' };
      setError(errorResponse.message);
      return errorResponse;
    } finally {
      setLoading(false);
    }
  };

  // Obtener permisos agrupados por módulo
  const getPermissionsByModule = useCallback(() => {
    const grouped = {};
    
    permissions.forEach(permission => {
      const module = permission.module || 'system';
      if (!grouped[module]) {
        grouped[module] = {
          name: module,
          displayName: getModuleDisplayName(module),
          icon: getModuleIcon(module),
          permissions: []
        };
      }
      grouped[module].permissions.push(permission);
    });

    // Ordenar permisos dentro de cada módulo
    Object.values(grouped).forEach(module => {
      module.permissions.sort((a, b) => a.name.localeCompare(b.name));
    });

    return grouped;
  }, [permissions]);

  // Obtener permisos agrupados por grupo dentro de módulo
  const getPermissionsByModuleAndGroup = useCallback(() => {
    const moduleGroups = {};
    
    permissions.forEach(permission => {
      const module = permission.module || 'system';
      const group = permission.group || 'general';
      
      if (!moduleGroups[module]) {
        moduleGroups[module] = {
          name: module,
          displayName: getModuleDisplayName(module),
          icon: getModuleIcon(module),
          groups: {}
        };
      }
      
      if (!moduleGroups[module].groups[group]) {
        moduleGroups[module].groups[group] = {
          name: group,
          displayName: getGroupDisplayName(group),
          permissions: []
        };
      }
      
      moduleGroups[module].groups[group].permissions.push(permission);
    });

    // Ordenar grupos y permisos
    Object.values(moduleGroups).forEach(module => {
      Object.values(module.groups).forEach(group => {
        group.permissions.sort((a, b) => a.name.localeCompare(b.name));
      });
    });

    return moduleGroups;
  }, [permissions]);

  // Actualizar filtros
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    // Resetear a la primera página cuando se cambian los filtros
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, []);

  // Cambiar página
  const changePage = useCallback((page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  }, []);

  // Cambiar items por página
  const changeItemsPerPage = useCallback((itemsPerPage) => {
    setPagination(prev => ({
      ...prev,
      itemsPerPage,
      currentPage: 1 // Resetear a la primera página
    }));
  }, []);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      module: '',
      group: '',
      sort_by: 'name',
      sort_order: 'asc'
    });
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  }, []);

  // Efectos
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Obtener módulos únicos
  const permissionModules = [...new Set(permissions.map(p => p.module))].sort();

  return {
    // Estado
    permissions,
    permissionModules,
    loading,
    error,
    pagination,
    filters,

    // Acciones
    loadPermissions,
    createPermission,
    updatePermission,
    deletePermission,
    updateFilters,
    changePage,
    changeItemsPerPage,
    clearFilters,

    // Utilidades
    getPermissionsByModule,
    getPermissionsByModuleAndGroup,
    setError
  };
};

/**
 * Hook para obtener un permiso específico
 */
export const usePermission = (permissionId) => {
  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPermission = useCallback(async () => {
    if (!permissionId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await permissionsService.getPermissionById(permissionId);

      if (response.success) {
        setPermission(response.data);
      } else {
        setError(response.message);
        setPermission(null);
      }
    } catch (error) {
      console.error('Error loading permission:', error);
      setError('Error cargando permiso');
      setPermission(null);
    } finally {
      setLoading(false);
    }
  }, [permissionId]);

  useEffect(() => {
    loadPermission();
  }, [loadPermission]);

  return {
    permission,
    loading,
    error,
    loadPermission,
    setPermission
  };
};

/**
 * Hook para estadísticas de permisos
 */
export const usePermissionStats = () => {
  const [stats, setStats] = useState({
    total: 0,
    byModule: {},
    byGroup: {},
    assigned: 0,
    unassigned: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await permissionsService.getPermissionStats();

      if (response.success) {
        setStats(response.data || {
          total: 0,
          byModule: {},
          byGroup: {},
          assigned: 0,
          unassigned: 0
        });
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error('Error loading permission stats:', error);
      setError('Error cargando estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    stats,
    loading,
    error,
    loadStats
  };
};

// Funciones auxiliares para traducción de permisos
const getPermissionDisplayName = (permName) => {
  const translations = {
    // Admin y sistema
    'admin_access': 'Acceso al Panel de Administración',
    'manage_users': 'Gestionar Usuarios',
    'manage_roles': 'Gestionar Roles',
    'manage_permissions': 'Gestionar Permisos',
    'view_system_settings': 'Ver Configuración del Sistema',
    'edit_system_settings': 'Editar Configuración del Sistema',

    // Usuarios
    'create_users': 'Crear Usuarios',
    'view_users': 'Ver Usuarios',
    'edit_users': 'Editar Usuarios',
    'delete_users': 'Eliminar Usuarios',
    'manage_user_roles': 'Gestionar Roles de Usuarios',

    // Roles
    'create_roles': 'Crear Roles',
    'view_roles': 'Ver Roles',
    'edit_roles': 'Editar Roles',
    'delete_roles': 'Eliminar Roles',
    'assign_permissions': 'Asignar Permisos',

    // Clientes
    'create_clientes': 'Crear Clientes',
    'view_clientes': 'Ver Clientes',
    'edit_clientes': 'Editar Clientes',
    'delete_clientes': 'Eliminar Clientes',
    'manage_clientes': 'Gestionar Clientes',

    // Productos
    'create_productos': 'Crear Productos',
    'view_productos': 'Ver Productos',
    'edit_productos': 'Editar Productos',
    'delete_productos': 'Eliminar Productos',
    'manage_productos': 'Gestionar Productos',
    'view_formulaciones': 'Ver Formulaciones',
    'edit_formulaciones': 'Editar Formulaciones',

    // Inventario
    'view_inventario': 'Ver Inventario',
    'edit_inventario': 'Editar Inventario',
    'manage_inventario': 'Gestionar Inventario',
    'adjust_stock': 'Ajustar Stock',

    // Ventas
    'create_ventas': 'Crear Ventas',
    'view_ventas': 'Ver Ventas',
    'edit_ventas': 'Editar Ventas',
    'delete_ventas': 'Eliminar Ventas',
    'manage_ventas': 'Gestionar Ventas',
    'approve_ventas': 'Aprobar Ventas',

    // Compras
    'create_compras': 'Crear Compras',
    'view_compras': 'Ver Compras',
    'edit_compras': 'Editar Compras',
    'delete_compras': 'Eliminar Compras',
    'manage_compras': 'Gestionar Compras',
    'approve_compras': 'Aprobar Compras',

    // Proveedores
    'create_proveedores': 'Crear Proveedores',
    'view_proveedores': 'Ver Proveedores',
    'edit_proveedores': 'Editar Proveedores',
    'delete_proveedores': 'Eliminar Proveedores',
    'manage_proveedores': 'Gestionar Proveedores',

    // Reportes
    'view_reportes': 'Ver Reportes',
    'export_reportes': 'Exportar Reportes',
    'generate_reportes': 'Generar Reportes',
    'manage_reportes': 'Gestionar Reportes',

    // Vendedores
    'create_vendedores': 'Crear Vendedores',
    'view_vendedores': 'Ver Vendedores',
    'edit_vendedores': 'Editar Vendedores',
    'delete_vendedores': 'Eliminar Vendedores',
    'manage_vendedores': 'Gestionar Vendedores',

    // Órdenes
    'create_ordenes': 'Crear Órdenes',
    'view_ordenes': 'Ver Órdenes',
    'edit_ordenes': 'Editar Órdenes',
    'delete_ordenes': 'Eliminar Órdenes',
    'manage_ordenes': 'Gestionar Órdenes',
    'approve_ordenes': 'Aprobar Órdenes'
  };

  // Si existe traducción directa, usarla
  if (translations[permName]) {
    return translations[permName];
  }

  // Si no existe, intentar generar una traducción automática
  const parts = permName.split('_');
  const action = parts[0];
  const resource = parts.slice(1).join('_');

  const actionTranslations = {
    'create': 'Crear',
    'view': 'Ver',
    'edit': 'Editar',
    'delete': 'Eliminar',
    'manage': 'Gestionar',
    'approve': 'Aprobar',
    'export': 'Exportar',
    'generate': 'Generar',
    'assign': 'Asignar',
    'admin': 'Administrar'
  };

  const resourceTranslations = {
    'users': 'Usuarios',
    'roles': 'Roles',
    'permissions': 'Permisos',
    'clientes': 'Clientes',
    'productos': 'Productos',
    'ventas': 'Ventas',
    'compras': 'Compras',
    'inventario': 'Inventario',
    'proveedores': 'Proveedores',
    'reportes': 'Reportes',
    'vendedores': 'Vendedores',
    'ordenes': 'Órdenes',
    'formulaciones': 'Formulaciones',
    'stock': 'Stock',
    'access': 'Acceso',
    'settings': 'Configuración',
    'system': 'Sistema'
  };

  const translatedAction = actionTranslations[action] || action;
  const translatedResource = resourceTranslations[resource] || resource;

  return `${translatedAction} ${translatedResource}`;
};

const inferModuleFromPermission = (permName) => {
  const parts = permName.split('_');

  // Revisar si contiene algún módulo conocido
  const moduleKeywords = {
    'users': 'usuarios',
    'roles': 'roles',
    'permissions': 'permisos',
    'clientes': 'clientes',
    'productos': 'productos',
    'ventas': 'ventas',
    'compras': 'compras',
    'inventario': 'inventario',
    'proveedores': 'proveedores',
    'reportes': 'reportes',
    'vendedores': 'vendedores',
    'ordenes': 'ordenes',
    'admin': 'admin',
    'system': 'sistema'
  };

  for (const part of parts) {
    if (moduleKeywords[part]) {
      return moduleKeywords[part];
    }
  }

  // Si es el segundo elemento, usarlo
  if (parts.length > 1) {
    return moduleKeywords[parts[1]] || parts[1];
  }

  return 'sistema';
};

const inferGroupFromPermission = (permName) => {
  const action = permName.split('_')[0];

  const groupMapping = {
    'create': 'Crear',
    'view': 'Ver',
    'edit': 'Editar',
    'delete': 'Eliminar',
    'manage': 'Administrar',
    'approve': 'Aprobar',
    'export': 'Exportar',
    'generate': 'Generar',
    'assign': 'Asignar',
    'admin': 'Administrar'
  };

  return groupMapping[action] || 'General';
};

const getPermissionDescription = (permName) => {
  const displayName = getPermissionDisplayName(permName);
  return `Permiso para: ${displayName}`;
};

// Funciones auxiliares
const getModuleDisplayName = (module) => {
  const moduleNames = {
    users: 'Usuarios',
    roles: 'Roles',
    permissions: 'Permisos',
    clientes: 'Clientes',
    productos: 'Productos',
    ventas: 'Ventas',
    compras: 'Compras',
    inventario: 'Inventario',
    proveedores: 'Proveedores',
    reportes: 'Reportes',
    system: 'Sistema',
    admin: 'Administración'
  };
  
  return moduleNames[module] || module.charAt(0).toUpperCase() + module.slice(1);
};

const getModuleIcon = (module) => {
  const moduleIcons = {
    users: 'fas fa-users',
    roles: 'fas fa-user-shield',
    permissions: 'fas fa-key',
    clientes: 'fas fa-user-tie',
    productos: 'fas fa-box',
    ventas: 'fas fa-shopping-cart',
    compras: 'fas fa-shopping-bag',
    inventario: 'fas fa-warehouse',
    proveedores: 'fas fa-truck',
    reportes: 'fas fa-chart-bar',
    system: 'fas fa-cogs',
    admin: 'fas fa-shield-alt'
  };
  
  return moduleIcons[module] || 'fas fa-cog';
};

const getGroupDisplayName = (group) => {
  const groupNames = {
    create: 'Crear',
    read: 'Ver',
    update: 'Editar',
    delete: 'Eliminar',
    manage: 'Administrar',
    general: 'General',
    advanced: 'Avanzado',
    reports: 'Reportes'
  };
  
  return groupNames[group] || group.charAt(0).toUpperCase() + group.slice(1);
};

export default usePermissions;
